import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AIAnalyzeModal from './AIAnalyzeModal';

// ── 모바일 감지 훅 ──────────────────────────────────────────
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

// ── 색상 토큰 ────────────────────────────────────────────────
const BLUE    = '#3a8fd4';
const BLUE_BG = '#e6f1fb';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const getUserId = () => {
  try {
    const sessionUser = JSON.parse(sessionStorage.getItem('user'));
    if (sessionUser?.userId) return String(sessionUser.userId);
    if (sessionUser?.id)     return String(sessionUser.id);
    if (sessionUser?.email)  return sessionUser.email;
    const localUser = JSON.parse(localStorage.getItem('currentUser'));
    if (localUser?.userId) return String(localUser.userId);
    if (localUser?.id)     return String(localUser.id);
    if (localUser?.email)  return localUser.email;
  } catch (e) {}
  return 'guest';
};

const getUserName = () => {
  try {
    const sessionUser = JSON.parse(sessionStorage.getItem('user'));
    const name = sessionUser?.name || JSON.parse(localStorage.getItem('currentUser'))?.name;
    if (name && name !== '회원가입') return name;
  } catch (e) {}
  return '사용자';
};

const Ico = {
  Folder: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>,
  Bell:   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>,
  Chart:  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
  Tool:   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>,
};

function CardHeader({ icon, title }) {
  return (
    <div style={S.cardHeader}>
      <div style={S.cardIcon}>{icon}</div>
      <h2 style={S.cardTitle}>{title}</h2>
    </div>
  );
}

function TeamProjectPage() {
  const navigate  = useNavigate();
  const isMobile  = useIsMobile();

  const [userName, setUserName] = useState(getUserName);
  const [teams,    setTeams]    = useState(() => {
    const userId = getUserId();
    try { const saved = localStorage.getItem(`allTeams_${userId}`); return saved ? JSON.parse(saved) : []; } catch (e) { return []; }
  });
  const [selectedId,         setSelectedId]         = useState(() => teams[0]?.id || null);
  const [showAddModal,       setShowAddModal]       = useState(false);
  const [newTeamName,        setNewTeamName]        = useState('');
  const [newProjectTitle,    setNewProjectTitle]    = useState('');
  const [newDeadline,        setNewDeadline]        = useState('');
  const [contextMenu,        setContextMenu]        = useState({ show: false, x: 0, y: 0, teamId: null, feedId: null });
  const [minutesModal,       setMinutesModal]       = useState({ show: false, mode: 'list', data: null });
  const [feedInput,          setFeedInput]          = useState('');
  const [feedType,           setFeedType]           = useState('공지');
  const [isWritingFeed,      setIsWritingFeed]      = useState(false);
  const [isAIModalOpen,      setIsAIModalOpen]      = useState(false);

  useEffect(() => {
    setUserName(getUserName());
    const userId = getUserId();
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';

    // 백엔드에서 내가 속한 팀 목록 받아와서 동기화
    fetch(`${apiUrl}/api/teams/${userId}`)
        .then(res => res.json())
        .then(backendTeams => {
          if (Array.isArray(backendTeams)) {
            const backendIds = backendTeams.map(t => String(t.id));
            const saved = localStorage.getItem(`allTeams_${userId}`);
            const localTeams = saved ? JSON.parse(saved) : [];

            // 백엔드에 없는 팀은 제거 (삭제된 팀원 처리)
            const synced = localTeams.filter(t => backendIds.includes(String(t.id)));

            // 백엔드에만 있는 팀은 추가 (다른 기기에서 합류한 경우)
            backendTeams.forEach(bt => {
              if (!synced.some(t => String(t.id) === String(bt.id))) {
                synced.push({
                  id: bt.id,
                  name: bt.name,
                  projectTitle: bt.name,
                  progress: 0,
                  status: bt.status || '준비',
                  deadline: bt.deadline || '2026-12-31',
                  inviteCode: '',
                  members: [],
                  feeds: [],
                  meetingMinutes: [],
                });
              }
            });

            localStorage.setItem(`allTeams_${userId}`, JSON.stringify(synced));
            setTeams(synced);
          }
        })
        .catch(() => {
          // 백엔드 실패 시 localStorage 그대로 사용
          try {
            const saved = localStorage.getItem(`allTeams_${userId}`);
            if (saved) setTeams(JSON.parse(saved));
          } catch (e) {}
        });

    const syncData = () => {
      try { const saved = localStorage.getItem(`allTeams_${userId}`); if (saved) setTeams(JSON.parse(saved)); } catch (e) {}
    };
    window.addEventListener('focus', syncData);
    return () => window.removeEventListener('focus', syncData);
  }, []);

  useEffect(() => {
    const userId = getUserId();
    localStorage.setItem(`allTeams_${userId}`, JSON.stringify(teams));
  }, [teams]);

  useEffect(() => {
    if (teams.length > 0 && !teams.find(t => String(t.id) === String(selectedId))) {
      setSelectedId(teams[0].id);
    }
  }, [teams]);

  const currentTeam = teams.find(t => String(t.id) === String(selectedId)) || teams[0];

  const getDDay = (date) => {
    const diff = new Date(date) - new Date();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return days >= 0 ? `D-${days}` : `D+${Math.abs(days)}`;
  };

  const getStatusBadgeStyle = (status) => {
    if (status?.includes('준비'))    return { backgroundColor: '#f1f5f9', color: '#64748b' };
    if (status?.includes('기획'))    return { backgroundColor: '#fef9c3', color: '#854d0e' };
    if (status?.includes('개발완료')) return { backgroundColor: '#dcfce7', color: '#166534' };
    if (status?.includes('개발중'))  return { backgroundColor: BLUE_BG,   color: BLUE };
    return { backgroundColor: BLUE_BG, color: BLUE };
  };

  const getFeedBadgeStyle = (type) => {
    if (type === '공지') return { backgroundColor: '#fee2e2', color: '#b91c1c' };
    if (type === '완료') return { backgroundColor: '#dcfce7', color: '#166634' };
    return { backgroundColor: BLUE_BG, color: BLUE };
  };

  const handleAddTeam = async () => {
    if (!newTeamName || !newProjectTitle) return;
    const uid         = getUserId();
    const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
    const apiUrl      = process.env.REACT_APP_API_URL || 'http://localhost:8080';
    let backendTeamId = null;
    try {
      const res = await fetch(`${apiUrl}/api/teams/${currentUser.userId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newTeamName, description: newProjectTitle, deadline: newDeadline || '2026-12-31' }),
      });
      const data = await res.json();
      backendTeamId = data.id;
    } catch (e) {}

    const newId      = backendTeamId || Date.now();
    const inviteCode = `${newId.toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    const newTeam    = {
      id: newId, name: newTeamName, projectTitle: newProjectTitle,
      progress: 0, status: '준비', deadline: newDeadline || '2026-12-31',
      inviteCode, creatorUniversity: currentUser.university || '',
      members: [{ name: userName, role: '미정', isLeader: true, task: '할 일을 등록하세요', contribution: 100 }],
      feeds: [], meetingMinutes: [],
    };
    setTeams(prev => [...prev, newTeam]);
    setSelectedId(newId);
    setNewTeamName(''); setNewProjectTitle(''); setNewDeadline('');
    setShowAddModal(false);
    if (backendTeamId) {
      try { await fetch(`${apiUrl}/api/teams/${backendTeamId}/invite`, { method: 'POST' }); } catch (e) {}
    }
  };

  const handleContextMenu     = (e, teamId) => { e.preventDefault(); setContextMenu({ show: true, x: e.pageX, y: e.pageY, teamId, feedId: null }); };
  const handleFeedContextMenu = (e, feedId) => { e.preventDefault(); e.stopPropagation(); setContextMenu({ show: true, x: e.pageX, y: e.pageY, teamId: null, feedId }); };

  const handleDeleteTeam = async () => {
    const targetId = contextMenu.teamId;
    const targetTeam = teams.find(t => String(t.id) === String(targetId));
    const isLeader = targetTeam?.members?.some(m => m.name === userName && m.isLeader);

    const updated = teams.filter(t => t.id !== targetId);
    setTeams(updated);
    if (String(selectedId) === String(targetId)) setSelectedId(updated[0]?.id || null);
    setContextMenu(prev => ({ ...prev, show: false }));

    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
    const uid = getUserId();

    if (isLeader) {
      fetch(`${apiUrl}/api/teams/${targetId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      }).catch(() => {});

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith('allTeams_')) {
          try {
            const allTeams = JSON.parse(localStorage.getItem(key) || '[]');
            const filtered = allTeams.filter(t => String(t.id) !== String(targetId));
            localStorage.setItem(key, JSON.stringify(filtered));
          } catch (e) {}
        }
      }
    } else {
      await fetch(`${apiUrl}/api/teams/${targetId}/leave`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', 'X-User-Id': uid },
      }).catch(() => {});

      const myTeams = JSON.parse(localStorage.getItem(`allTeams_${uid}`) || '[]');
      localStorage.setItem(`allTeams_${uid}`, JSON.stringify(
          myTeams.filter(t => String(t.id) !== String(targetId))
      ));
    }
  };

  const handleDeleteFeed = () => {
    setTeams(teams.map(t => String(t.id) === String(selectedId) ? { ...t, feeds: t.feeds.filter(f => f.id !== contextMenu.feedId) } : t));
    setContextMenu(prev => ({ ...prev, show: false }));
  };

  const handleClearAllFeeds = () => {
    if (window.confirm('정말 모든 피드 기록을 삭제하시겠습니까?')) {
      setTeams(teams.map(t => String(t.id) === String(selectedId) ? { ...t, feeds: [] } : t));
    }
  };

  const handleGoToDetail = () => { if (currentTeam) navigate('/team-management', { state: { team: currentTeam } }); };

  const handleSaveFeed = () => {
    if (!feedInput) return;
    const newFeed = { id: Date.now(), type: feedType, text: feedInput, time: '방금 전', user: userName };
    setTeams(teams.map(t => String(t.id) === String(selectedId) ? { ...t, feeds: [newFeed, ...t.feeds] } : t));
    setFeedInput(''); setIsWritingFeed(false);
  };

  const handleSaveMinutes = (data) => {
    const teamMinutes = currentTeam.meetingMinutes || [];
    let updatedMinutes;
    if (minutesModal.mode === 'add') {
      updatedMinutes = [...teamMinutes, { ...data, id: Date.now(), date: new Date().toLocaleDateString() }];
    } else {
      updatedMinutes = teamMinutes.map(m => m.id === data.id ? data : m);
    }
    setTeams(teams.map(t => String(t.id) === String(selectedId) ? { ...t, meetingMinutes: updatedMinutes } : t));
    setMinutesModal({ show: true, mode: 'list' });
  };

  const handleDeleteMinutes = (id) => {
    const updatedMinutes = (currentTeam.meetingMinutes || []).filter(m => m.id !== id);
    setTeams(teams.map(t => String(t.id) === String(selectedId) ? { ...t, meetingMinutes: updatedMinutes } : t));
    setMinutesModal({ show: true, mode: 'list' });
  };

  useEffect(() => {
    const handleClick = () => setContextMenu(prev => ({ ...prev, show: false }));
    window.addEventListener('click', handleClick);
    return () => window.removeEventListener('click', handleClick);
  }, []);

  const contribColors = [BLUE, '#60a5fa', '#93c5fd', '#bfdbfe'];

  return (
    <div style={{ ...S.root, overflowX: 'hidden' }} onClick={() => setContextMenu(prev => ({ ...prev, show: false }))}>
      <main style={{
        ...S.main,
        padding: isMobile ? '16px 14px 60px' : '32px 32px 60px',
      }}>

        <h2 style={{ ...S.pageTitle, fontSize: isMobile ? '18px' : '22px' }}>👥 팀 프로젝트 관리</h2>

        {teams.length === 0 ? (
          <div style={S.emptyWrap}>
            <p style={S.emptyText}>아직 참여한 팀이 없어요!</p>
            <button style={S.primaryBtn} onClick={() => setShowAddModal(true)}>+ 팀 만들기</button>
          </div>
        ) : (
          <div style={{
            ...S.mainGrid,
            // 모바일: 1열
            gridTemplateColumns: isMobile ? '1fr' : '1.3fr 0.7fr',
            gap: isMobile ? '14px' : '20px',
          }}>

            {/* ── 왼쪽 (모바일에서는 오른쪽 컬럼이 먼저 오도록 order 활용) ── */}
            {/* 모바일: 팀 목록 + 피드 먼저, 그 다음 상세 카드들 */}

            {/* 팀 목록 카드 — 모바일에서 최상단 */}
            {isMobile && (
              <div style={S.card}>
                <div style={{ ...S.cardHeader, justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={S.cardIcon}>{Ico.Folder}</div>
                    <h2 style={S.cardTitle}>팀 목록</h2>
                  </div>
                  <button style={S.addBtn} onClick={() => setShowAddModal(true)}>+</button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {teams.map(team => (
                    <div
                      key={team.id}
                      style={{ ...S.teamItem, borderColor: String(selectedId) === String(team.id) ? BLUE : '#e2e8f0', backgroundColor: String(selectedId) === String(team.id) ? '#f0f7ff' : '#fff' }}
                      onClick={() => setSelectedId(team.id)}
                      onContextMenu={(e) => handleContextMenu(e, team.id)}
                    >
                      <div>
                        <div style={S.teamName}>팀: {team.name || '이름 정보 없음'}</div>
                        <div style={S.teamSubject}>주제: {team.projectTitle || '주제 정보 없음'}</div>
                        <div style={S.teamDate}>{team.deadline} 까지</div>
                      </div>
                      <span style={{ color: String(selectedId) === String(team.id) ? BLUE : '#e2e8f0', fontSize: '10px' }}>●</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── 왼쪽 컬럼 ── */}
            <div style={S.leftCol}>

              {/* 프로젝트 일정 카드 */}
              <section style={S.scheduleCard}>
                <div style={S.scheduleHeader}>
                  <h3 style={S.scheduleTitle}>📅 프로젝트 일정</h3>
                  <span style={S.ddayBadge}>{currentTeam ? getDDay(currentTeam.deadline) : '-'}</span>
                </div>
                <p style={S.deadlineText}>최종 마감일: <strong>{currentTeam?.deadline}</strong></p>
                <div style={{
                  ...S.actionGrid,
                  // 모바일: 3열 유지 (버튼이 작아도 3열이 더 자연스러움)
                  gridTemplateColumns: 'repeat(3, 1fr)',
                }}>
                  <button style={S.actionBtn} onClick={() => window.open('https://drive.google.com')}>📂 공유 폴더</button>
                  <button style={S.actionBtn} onClick={() => window.open('https://zoom.us')}>📹 화상 회의</button>
                  <button style={S.actionBtn} onClick={() => setMinutesModal({ show: true, mode: 'list' })}>📝 회의록</button>
                </div>
              </section>

              {/* 프로젝트 현황 카드 */}
              <section style={S.card}>
                <div style={{
                  ...S.projHeader,
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? '10px' : '0',
                }}>
                  <div>
                    <div style={S.projTeam}>{currentTeam?.name}</div>
                    <h3 style={{ ...S.projTitle, fontSize: isMobile ? '15px' : '18px' }}>{currentTeam?.projectTitle}</h3>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ ...S.statusBadge, ...getStatusBadgeStyle(currentTeam?.status) }}>{currentTeam?.status}</span>
                    <button style={S.detailBtn} onClick={handleGoToDetail}>상세관리</button>
                  </div>
                </div>
                <div style={S.gaugeRow}>
                  <span style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>진행률</span>
                  <span style={{ fontSize: '12px', fontWeight: '700', color: BLUE }}>{currentTeam?.progress}%</span>
                </div>
                <div style={S.gaugeTrack}>
                  <div style={{ ...S.gaugeFill, width: `${currentTeam?.progress}%` }} />
                </div>
                {/* 멤버 테이블 — 모바일: 폰트 축소 */}
                <div style={{ ...S.memberHeader, fontSize: isMobile ? '10px' : '11px' }}>
                  <span style={{ flex: 1.2 }}>팀원</span>
                  <span style={{ flex: 1.5 }}>역할</span>
                  <span style={{ flex: 2, textAlign: 'right' }}>현재 작업</span>
                </div>
                {currentTeam?.members?.map((member, idx) => (
                  <div key={idx} style={{ ...S.memberRow, backgroundColor: member.name === userName ? '#fff7ed' : 'transparent', fontSize: isMobile ? '11px' : '12px' }}>
                    <span style={{ flex: 1.2, fontWeight: '700', color: '#1e293b' }}>
                      {member.name} {(member.isLeader || member.role?.includes('팀장')) && '👑'}
                    </span>
                    <span style={{ flex: 1.5, fontSize: isMobile ? '10px' : '12px', color: '#64748b' }}>{member.role}</span>
                    <span style={{ flex: 2, textAlign: 'right', fontSize: isMobile ? '10px' : '12px', color: member.name === userName ? '#ea580c' : '#374151', fontWeight: member.name === userName ? '700' : '500' }}>
                      {member.task}
                    </span>
                  </div>
                ))}
              </section>

              {/* 도구함 카드 */}
              <section style={S.card}>
                <CardHeader icon={Ico.Tool} title="팀플 도구함" />
                <div style={{
                  ...S.toolGrid,
                  // 모바일: 2열
                  gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
                  gap: isMobile ? '8px' : '8px',
                }}>
                  {['사다리 타기', '제비뽑기', '돌림판', '랜덤 할당'].map((tool, i) => (
                    <div key={i} style={S.toolItem} onClick={() => navigate(`/${['ladder-game','draw-lots','roulette','random-assign'][i]}`)}>
                      {tool}
                    </div>
                  ))}
                </div>
              </section>

              {/* 기여도 분석 리포트 */}
              <section style={S.card}>
                <CardHeader icon={Ico.Chart} title="기여도 분석 리포트" />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  {currentTeam?.members?.map((member, idx) => (
                    <div key={idx} style={S.contribRow}>
                      <span style={S.contribName}>{member.name}</span>
                      <div style={S.contribTrack}>
                        <div style={{ ...S.contribFill, width: `${member.contribution}%`, backgroundColor: contribColors[idx % contribColors.length] }} />
                      </div>
                      <span style={S.contribPct}>{member.contribution}%</span>
                    </div>
                  ))}
                </div>
                <button style={S.aiBtn} onClick={() => setIsAIModalOpen(true)}>✨ AI 기여도 실시간 업데이트</button>
              </section>
            </div>

            {/* ── 오른쪽 컬럼 (데스크탑만 표시, 모바일은 위에서 별도 렌더링) ── */}
            <div style={{ ...S.rightCol, display: isMobile ? 'none' : 'flex' }}>

              {/* 팀 목록 카드 */}
              <div style={S.card}>
                <div style={{ ...S.cardHeader, justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={S.cardIcon}>{Ico.Folder}</div>
                    <h2 style={S.cardTitle}>팀 목록</h2>
                  </div>
                  <button style={S.addBtn} onClick={() => setShowAddModal(true)}>+</button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {teams.map(team => (
                    <div
                      key={team.id}
                      style={{ ...S.teamItem, borderColor: String(selectedId) === String(team.id) ? BLUE : '#e2e8f0', backgroundColor: String(selectedId) === String(team.id) ? '#f0f7ff' : '#fff' }}
                      onClick={() => setSelectedId(team.id)}
                      onContextMenu={(e) => handleContextMenu(e, team.id)}
                    >
                      <div>
                        <div style={S.teamName}>팀: {team.name || '이름 정보 없음'}</div>
                        <div style={S.teamSubject}>주제: {team.projectTitle || '주제 정보 없음'}</div>
                        <div style={S.teamDate}>{team.deadline} 까지</div>
                      </div>
                      <span style={{ color: String(selectedId) === String(team.id) ? BLUE : '#e2e8f0', fontSize: '10px' }}>●</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* 팀 피드 카드 */}
              <div style={{ ...S.card, borderTop: `3px solid ${BLUE}` }}>
                <div style={{ ...S.cardHeader, justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={S.cardIcon}>{Ico.Bell}</div>
                    <h2 style={S.cardTitle}>팀 피드</h2>
                  </div>
                  <button style={S.delAllBtn} onClick={handleClearAllFeeds}>전체삭제</button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '200px', overflowY: 'auto' }}>
                  {currentTeam?.feeds?.map(feed => (
                    <div key={feed.id} style={S.feedItem} onContextMenu={(e) => handleFeedContextMenu(e, feed.id)}>
                      <span style={{ ...S.feedBadge, ...getFeedBadgeStyle(feed.type) }}>{feed.type}</span>
                      <div>
                        <div style={S.feedText}>{feed.text}</div>
                        <div style={S.feedTime}>{feed.time} · {feed.user}</div>
                      </div>
                    </div>
                  ))}
                  {(!currentTeam?.feeds || currentTeam.feeds.length === 0) && (
                    <p style={S.emptyTextSm}>활동 기록이 없습니다.</p>
                  )}
                </div>
                {isWritingFeed ? (
                  <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <select style={S.feedSelect} value={feedType} onChange={(e) => setFeedType(e.target.value)}>
                        <option>공지</option>
                        <option>진행</option>
                        <option>완료</option>
                      </select>
                      <input style={S.feedInput} placeholder="내용 입력... (최대 100자)" value={feedInput} onChange={(e) => { if (e.target.value.length <= 100) setFeedInput(e.target.value); }} maxLength={100} />
                      <button style={S.feedSaveBtn} onClick={handleSaveFeed}>저장</button>
                    </div>
                  </div>
                ) : (
                  <button style={S.recordBtn} onClick={() => setIsWritingFeed(true)}>활동 기록하기</button>
                )}
              </div>
            </div>

            {/* 팀 피드 — 모바일 전용 (왼쪽 컬럼 아래) */}
            {isMobile && (
              <div style={{ ...S.card, borderTop: `3px solid ${BLUE}` }}>
                <div style={{ ...S.cardHeader, justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={S.cardIcon}>{Ico.Bell}</div>
                    <h2 style={S.cardTitle}>팀 피드</h2>
                  </div>
                  <button style={S.delAllBtn} onClick={handleClearAllFeeds}>전체삭제</button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '200px', overflowY: 'auto' }}>
                  {currentTeam?.feeds?.map(feed => (
                    <div key={feed.id} style={S.feedItem} onContextMenu={(e) => handleFeedContextMenu(e, feed.id)}>
                      <span style={{ ...S.feedBadge, ...getFeedBadgeStyle(feed.type) }}>{feed.type}</span>
                      <div>
                        <div style={S.feedText}>{feed.text}</div>
                        <div style={S.feedTime}>{feed.time} · {feed.user}</div>
                      </div>
                    </div>
                  ))}
                  {(!currentTeam?.feeds || currentTeam.feeds.length === 0) && (
                    <p style={S.emptyTextSm}>활동 기록이 없습니다.</p>
                  )}
                </div>
                {isWritingFeed ? (
                  <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      <select style={S.feedSelect} value={feedType} onChange={(e) => setFeedType(e.target.value)}>
                        <option>공지</option>
                        <option>진행</option>
                        <option>완료</option>
                      </select>
                      <input style={{ ...S.feedInput, flex: 1, minWidth: 0 }} placeholder="내용 입력..." value={feedInput} onChange={(e) => setFeedInput(e.target.value)} />
                      <button style={S.feedSaveBtn} onClick={handleSaveFeed}>저장</button>
                    </div>
                  </div>
                ) : (
                  <button style={S.recordBtn} onClick={() => setIsWritingFeed(true)}>활동 기록하기</button>
                )}
              </div>
            )}

          </div>
        )}
      </main>

      {/* ── 컨텍스트 메뉴 ── */}
      {contextMenu.show && (
        <div style={{ ...S.contextMenu, top: contextMenu.y, left: contextMenu.x }}>
          {contextMenu.teamId !== null && (
              <div style={S.contextItemDel} onClick={handleDeleteTeam}>❌ 팀 삭제하기</div>
          )}
          {contextMenu.feedId !== null && <div style={S.contextItemDel} onClick={handleDeleteFeed}>❌ 피드 삭제하기</div>}
        </div>
      )}

      {/* ── 팀 추가 모달 ── */}
      {showAddModal && (
        <div style={S.modalOverlay}>
          <div style={S.modal}>
            <h3 style={S.modalTitle}>새 팀플 생성</h3>
            <input style={S.modalInput} placeholder="팀 이름을 입력하세요 (최대 20자)" value={newTeamName} onChange={(e) => { if (e.target.value.length <= 20) setNewTeamName(e.target.value); }} maxLength={20} />
            <input style={S.modalInput} placeholder="프로젝트 주제를 입력하세요 (최대 30자)" value={newProjectTitle} onChange={(e) => { if (e.target.value.length <= 30) setNewProjectTitle(e.target.value); }} maxLength={30} />
            <label style={S.modalLabel}>최종 마감일</label>
            <input type="date" style={{ ...S.modalInput, width: '100%', maxWidth: '100%', minWidth: 0, WebkitAppearance: 'none', appearance: 'none' }} value={newDeadline} min={new Date().toISOString().split('T')[0]} onChange={(e) => setNewDeadline(e.target.value)} />
            <div style={S.modalBtnRow}>
              <button style={S.primaryBtn} onClick={handleAddTeam}>저장</button>
              <button style={S.secondaryBtn} onClick={() => { setShowAddModal(false); setNewTeamName(''); setNewProjectTitle(''); setNewDeadline(''); }}>취소</button>
            </div>
          </div>
        </div>
      )}

      {/* ── 회의록 모달 ── */}
      {minutesModal.show && (
        <div style={S.modalOverlay}>
          <div style={{ ...S.modal, maxWidth: '480px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
              <h3 style={S.modalTitle}>📝 회의록 ({minutesModal.mode === 'list' ? '목록' : '상세'})</h3>
              <button style={{ border: 'none', background: 'none', fontSize: '20px', cursor: 'pointer', color: '#64748b' }} onClick={() => setMinutesModal({ show: false })}>×</button>
            </div>
            {minutesModal.mode === 'list' ? (
              <div>
                <button style={{ ...S.primaryBtn, width: '100%', marginBottom: '12px' }} onClick={() => setMinutesModal({ show: true, mode: 'add', data: { title: '', content: '' } })}>+ 새 회의록 작성</button>
                <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
                  {currentTeam?.meetingMinutes?.map(m => (
                    <div key={m.id} style={S.minutesItem} onClick={() => setMinutesModal({ show: true, mode: 'detail', data: m })}>
                      <span style={{ fontWeight: '600', fontSize: '13px', color: '#1e293b' }}>{m.title}</span>
                      <span style={{ fontSize: '11px', color: '#94a3b8' }}>{m.date}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <input style={S.modalInput} placeholder="회의 제목 (최대 30자)" defaultValue={minutesModal.data?.title} id="minTitle" maxLength={30} />
                <textarea style={{ ...S.modalInput, height: '130px', resize: 'vertical' }} placeholder="내용 (최대 500자)" defaultValue={minutesModal.data?.content} id="minContent" maxLength={500} />
                <div style={S.modalBtnRow}>
                  <button style={S.primaryBtn} onClick={() => handleSaveMinutes({ id: minutesModal.data?.id, title: document.getElementById('minTitle').value, content: document.getElementById('minContent').value, date: minutesModal.data?.date || new Date().toLocaleDateString() })}>저장</button>
                  {minutesModal.mode === 'detail' && <button style={{ ...S.secondaryBtn, color: '#ef4444', borderColor: '#fca5a5' }} onClick={() => handleDeleteMinutes(minutesModal.data.id)}>삭제</button>}
                  <button style={S.secondaryBtn} onClick={() => setMinutesModal({ show: true, mode: 'list' })}>취소</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <AIAnalyzeModal isOpen={isAIModalOpen} onClose={() => setIsAIModalOpen(false)} teamData={currentTeam} />
    </div>
  );
}

// ── 스타일 (원본 그대로) ─────────────────────────────────────
const S = {
  root: {
    minHeight: '100vh', backgroundColor: '#f8fafc',
    fontFamily: "'Pretendard', 'Apple SD Gothic Neo', sans-serif",
    display: 'flex', flexDirection: 'column', alignItems: 'center',
  },
  main: {
    width: '100%', maxWidth: '1100px',
    padding: '32px 32px 60px', boxSizing: 'border-box',
  },
  pageTitle: { fontSize: '22px', fontWeight: '800', color: '#1e293b', margin: '0 0 20px' },
  mainGrid:  { display: 'grid', gridTemplateColumns: '1.3fr 0.7fr', gap: '20px' },
  leftCol:   { display: 'flex', flexDirection: 'column', gap: '20px' },
  rightCol:  { display: 'flex', flexDirection: 'column', gap: '20px' },
  card: {
    backgroundColor: '#fff', borderRadius: '14px', padding: '20px 22px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.04)',
    border: '1px solid #f1f5f9', boxSizing: 'border-box', minWidth: 0,
  },
  cardHeader: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' },
  cardIcon: {
    width: '28px', height: '28px', borderRadius: '8px', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    backgroundColor: BLUE_BG, color: BLUE,
  },
  cardTitle: { margin: 0, fontSize: '14px', fontWeight: '600', color: '#1e293b' },
  scheduleCard: {
    background: 'linear-gradient(135deg, #4aa3e8 0%, #3a8fd4 100%)',
    borderRadius: '14px', padding: '20px 22px', color: '#fff',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.04)',
    boxSizing: 'border-box',
  },
  scheduleHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' },
  scheduleTitle:  { fontSize: '14px', fontWeight: '600', color: '#fff', margin: 0 },
  ddayBadge:      { backgroundColor: 'rgba(255,255,255,0.25)', padding: '4px 10px', borderRadius: '8px', fontSize: '12px', fontWeight: '700', color: '#fff' },
  deadlineText:   { fontSize: '13px', color: 'rgba(255,255,255,0.85)', marginBottom: '14px' },
  actionGrid:     { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '8px' },
  actionBtn:      { padding: '10px 6px', backgroundColor: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', color: '#fff', fontSize: '12px', fontWeight: '600', textAlign: 'center', cursor: 'pointer' },
  projHeader:     { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' },
  projTeam:       { fontSize: '11px', color: BLUE, fontWeight: '700', marginBottom: '3px' },
  projTitle:      { fontSize: '18px', fontWeight: '800', color: '#1e293b', margin: 0 },
  statusBadge:    { padding: '3px 9px', borderRadius: '5px', fontSize: '11px', fontWeight: '700' },
  detailBtn:      { padding: '6px 12px', backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', color: '#374151' },
  gaugeRow:       { display: 'flex', justifyContent: 'space-between' },
  gaugeTrack:     { width: '100%', height: '6px', backgroundColor: '#e2e8f0', borderRadius: '99px', overflow: 'hidden', margin: '6px 0 2px' },
  gaugeFill:      { height: '100%', borderRadius: '99px', backgroundColor: BLUE, transition: 'width 0.5s' },
  memberHeader:   { display: 'flex', padding: '8px 10px', fontSize: '11px', fontWeight: '700', color: BLUE, borderBottom: '1px solid #f1f5f9', marginTop: '12px' },
  memberRow:      { display: 'flex', alignItems: 'center', padding: '10px', borderRadius: '8px', fontSize: '12px' },
  toolGrid:       { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '8px' },
  toolItem:       { padding: '11px 4px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#374151', cursor: 'pointer' },
  contribRow:     { display: 'flex', alignItems: 'center', gap: '12px' },
  contribName:    { width: '52px', fontSize: '12px', fontWeight: '600', color: '#374151', flexShrink: 0 },
  contribTrack:   { flex: 1, height: '8px', backgroundColor: '#e2e8f0', borderRadius: '99px', overflow: 'hidden' },
  contribFill:    { height: '100%', borderRadius: '99px', transition: 'width 0.5s' },
  contribPct:     { fontSize: '12px', fontWeight: '700', color: BLUE, width: '36px', textAlign: 'right', flexShrink: 0 },
  aiBtn:          { width: '100%', padding: '13px', backgroundColor: '#1e293b', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', marginTop: '14px', boxSizing: 'border-box' },
  addBtn:         { width: '28px', height: '28px', backgroundColor: '#fff', border: `1px solid ${BLUE}`, color: BLUE, borderRadius: '7px', fontSize: '16px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  teamItem:       { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '13px 14px', border: '1px solid #e2e8f0', borderRadius: '10px', cursor: 'pointer' },
  teamName:       { fontSize: '13px', fontWeight: '700', color: '#1e293b' },
  teamSubject:    { fontSize: '11px', color: BLUE, fontWeight: '600', marginTop: '2px' },
  teamDate:       { fontSize: '10px', color: '#94a3b8', marginTop: '2px' },
  delAllBtn:      { padding: '4px 8px', backgroundColor: '#fff', border: '1px solid #fca5a5', color: '#ef4444', borderRadius: '5px', fontSize: '10px', fontWeight: '700', cursor: 'pointer' },
  feedItem:       { display: 'flex', gap: '10px' },
  feedBadge:      { padding: '3px 7px', borderRadius: '5px', fontSize: '10px', fontWeight: '700', flexShrink: 0, height: 'fit-content' },
  feedText:       { fontSize: '12px', fontWeight: '600', color: '#1e293b' },
  feedTime:       { fontSize: '10px', color: '#94a3b8', marginTop: '2px' },
  feedSelect:     { border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '11px', fontWeight: '700', padding: '4px 6px', color: '#374151' },
  feedInput:      { flex: 1, padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '12px', color: '#374151', outline: 'none', minWidth: 0 },
  feedSaveBtn:    { padding: '8px 12px', backgroundColor: BLUE, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '11px', fontWeight: '700', cursor: 'pointer' },
  recordBtn:      { width: '100%', padding: '11px', backgroundColor: '#1e293b', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', marginTop: '12px', boxSizing: 'border-box' },
  emptyWrap:      { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '100px 0', gap: '16px' },
  emptyText:      { color: '#94a3b8', fontSize: '16px', margin: 0 },
  emptyTextSm:    { fontSize: '12px', color: '#94a3b8', textAlign: 'center', padding: '10px 0', margin: 0 },
  primaryBtn:     { flex: 1, padding: '12px', backgroundColor: BLUE, color: '#fff', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  secondaryBtn:   { flex: 1, padding: '12px', backgroundColor: '#f1f5f9', color: '#374151', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  contextMenu:    { position: 'absolute', backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', boxShadow: '0 8px 20px rgba(0,0,0,0.12)', zIndex: 4000, padding: '6px 0', minWidth: '140px' },
  contextItemDel: { padding: '10px 16px', fontSize: '13px', color: '#ef4444', cursor: 'pointer', fontWeight: '600' },
  modalOverlay:   { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 3000 },
  modal:          { backgroundColor: '#fff', padding: '28px 32px', borderRadius: '14px', boxShadow: '0 20px 40px rgba(0,0,0,0.12)', width: '90%', maxWidth: '380px', boxSizing: 'border-box', overflow: 'hidden' },
  modalTitle:     { fontSize: '16px', fontWeight: '700', color: '#1e293b', margin: '0 0 16px' },
  modalLabel:     { fontSize: '12px', color: '#64748b', fontWeight: '600', marginBottom: '4px', display: 'block' },
  modalInput:     { width: '100%', maxWidth: '100%', padding: '11px 13px', marginBottom: '10px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px', color: '#374151', boxSizing: 'border-box', outline: 'none', backgroundColor: '#f8fafc', display: 'block' },
  modalBtnRow:    { display: 'flex', gap: '10px', marginTop: '16px' },
  minutesItem:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #f1f5f9', cursor: 'pointer' },
};

export default TeamProjectPage;